import { useEffect, useMemo, useState } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import { CountryEnum, CurrencyEnum, PlanTermEnum } from '@minecraft.graphql-operations';
import { CheckoutProps } from '../Checkout/types';
import { Purchase } from '../types';

// Define ApplePay types
type ApplePayPaymentRequest = {
  countryCode: string;
  currencyCode: string;
  total: {
    label: string;
    amount: string;
  };
  supportedNetworks: string[];
  merchantCapabilities: string[];
};

type ApplePayShippingMethod = {
  label: string;
  detail: string;
  amount: string;
  identifier: string;
};

type ApplePayLineItem = {
  label: string;
  amount: string;
  type?: 'final' | 'pending';
};

type ApplePayMerchantSession = {
  merchantIdentifier: string;
  domainName: string;
  displayName: string;
};

type ApplePaySession = {
  begin(): void;
  abort(): void;
  completeMerchantValidation(merchantSession: ApplePayMerchantSession): void;
  completePayment(status: number): void;
  completeShippingContactSelection(
    status: number,
    newShippingMethods: ApplePayShippingMethod[],
    newTotal: ApplePayLineItem,
    newLineItems: ApplePayLineItem[]
  ): void;
  completeShippingMethodSelection(status: number, newTotal: ApplePayLineItem, newLineItems: ApplePayLineItem[]): void;
};

type WindowWithApplePay = Window & {
  ApplePaySession?: {
    canMakePayments(): boolean;
    new (version: number, paymentRequest: ApplePayPaymentRequest): ApplePaySession;
  };
};

// Copied from Apple docs + Recurly docs
// https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypayment
// https://recurly.com/developers/reference/recurly-js/#apple-pay:~:text=simplify%20the%20integration.-,%5Boptions.callbacks.onPaymentAuthorized%5D,-Function
export type ApplePayPayment = {
  // This is the recurly addition:
  payment: {
    recurlyToken: {
      id: string;
      type: 'credit_card';
    };
  };
  // This is the stock Apple callback structure:
  token: {
    paymentMethod: {
      displayName?: string;
      network?: string;
      type?: 'credit' | 'debit' | 'prepaid' | 'store';
      paymentPass?: {
        primaryAccountIdentifier: string;
        primaryAccountNumberSuffix: string;
        deviceAccountIdentifier?: string;
        deviceAccountNumberSuffix?: string;
        activationState: 'activated' | 'requiresActivation' | 'activating' | 'suspended' | 'deactivated';
      };
      billingContact: {
        phoneNumber?: string;
        emailAddress?: string;
        givenName?: string;
        familyName?: string;
        phoneticGivenName?: string;
        phoneticFamilyName?: string;
        addressLines?: string[];
        subLocality?: string;
        locality?: string;
        postalCode?: string;
        subAdministrativeArea?: string;
        administrativeArea?: string;
        country?: string;
        countryCode?: string;
      };
    };
    transactionIdentifier?: string;
    paymentData: never;
  };
};

export type UseApplePayProps = {
  onPaymentAuthorized: (data: ApplePayPayment) => void;
  onError: (error: Error) => void;
  currency: CurrencyEnum;
  country: CountryEnum;
  currentPurchase?: CheckoutProps['currentPurchase'];
  selectedPlan?: CheckoutProps['selectedPlan'];
};

export const useApplePay = ({
  onPaymentAuthorized,
  currency = 'USD' as CurrencyEnum,
  country = 'US' as CountryEnum,
  onError,
  currentPurchase,
  selectedPlan,
}: UseApplePayProps) => {
  const recurly = useRecurly();
  const [showApplePayButton, setShowApplePayButton] = useState(false);

  const canApplePay: boolean = useMemo(() => {
    const isSupportedCountry = [CountryEnum.Us, CountryEnum.Ca].includes(country);
    return !!(window as WindowWithApplePay).ApplePaySession && isSupportedCountry;
  }, [country]);

  const applePay = useMemo(() => {
    if (canApplePay && selectedPlan && currentPurchase) {
      const recurringDate = new Date();
      const isCouponRedemption = (currentPurchase as Purchase)?.coupon;
      const isTrial = selectedPlan?.isTrialPeriodEnabled && !isCouponRedemption;
      recurringDate.setDate(recurringDate.getDate() + (isTrial ? selectedPlan.trialLengthDays : 0));

      // For recurring payments we need to set the recurringPaymentRequest correctly in Apple Pay
      // so that the Apple Pay can generate an MPAN token for these recurring payments
      // More details : https://docs.recurly.com/recurly-subscriptions/docs/apple-pay-on-the-web#best-practices-and-compliance
      // https://developer.apple.com/documentation/applepayontheweb/applepayrecurringpaymentrequest
      const billingInterval = {
        label: selectedPlan?.titleWithBillingInterval,
        paymentTiming: 'recurring',
        recurringPaymentStartDate: recurringDate,
        recurringPaymentIntervalUnit: selectedPlan?.term?.code === PlanTermEnum.Monthly ? 'month' : 'year',
        amount: isTrial || isCouponRedemption ? selectedPlan?.price : currentPurchase?.total,
      };

      const trialInterval = isTrial
        ? {
            paymentTiming: 'recurring',
            label: selectedPlan.titleWithBillingInterval,
            recurringPaymentIntervalUnit: 'day',
            recurringPaymentIntervalCount: selectedPlan?.trialLengthDays,
            recurringPaymentEndDate: recurringDate,
            amount: currentPurchase?.total,
          }
        : null;

      const totalAmount = isCouponRedemption ? currentPurchase?.total : billingInterval?.amount;

      return recurly.ApplePay({
        country,
        currency,
        callbacks: {
          onPaymentAuthorized,
        },
        paymentRequest: {
          total: {
            label: selectedPlan.titleWithBillingInterval,
            amount: trialInterval ? trialInterval.amount : totalAmount,
          },
          recurringPaymentRequest: {
            paymentDescription: 'Apple Pay via Recurly',
            regularBilling: { ...billingInterval },
            ...(trialInterval && { trialBilling: { ...trialInterval } }),
          },
        },
      });
    }

    return undefined;
  }, [canApplePay, country, currency, currentPurchase, onPaymentAuthorized, recurly, selectedPlan]);

  useEffect(() => {
    if (applePay) {
      applePay.on('ready', () => {
        setShowApplePayButton(true);
      });

      applePay.on('error', (err: Error) => {
        onError(err);
      });
    }
  }, [applePay, onError]);

  const onClick = () => {
    if (applePay) {
      applePay?.begin();
    }
  };

  return {
    showApplePayButton,
    onClick,
  };
};
